import { FormEvent, useState } from "react";

import { LinkButton } from "@/ui/next/LinkButton";
import Link from "@whoppah/next/link";
import { useTranslation } from "next-i18next";

import { useAuth } from "@/auth";
import {
  ACCOUNT_LOGIN_ROUTE,
  ACCOUNT_SETTINGS_ROUTE,
  ACCOUNT_SIGNUP_ROUTE,
  PRIVACY_ROUTE,
} from "@/common/static/routes.static";
import { cn } from "@/common/utils/css.utils";
import {
  ProBuyerApplicationInput,
  submitProBuyerApplication,
} from "@/features/pro-buyer-application/data/pro-buyer-application-client";
import { useProBuyerStatus } from "@/features/pro-buyer-application/hooks/useProBuyerStatus";
import {
  PRO_BUYER_APPLICATION_FIELDS,
  PRO_BUYER_APPLY_ANCHOR,
  PRO_BUYER_APPLY_RETURN_URL,
  PRO_BUYER_FIELD_MAX_LENGTH,
  PRO_BUYER_SCROLL_FLAG,
  ProBuyerApplicationField,
} from "@/features/pro-buyer-application/static/pro-buyer-application.static";
import useMe from "@/hooks/useMe";
import addToast from "@/toast/addToast";
import { InfoPanel } from "@/ui/InfoPanel";
import { Button } from "@/ui/cva/Button";
import { Heading } from "@/ui/cva/Heading";
import { InfoLabel } from "@/ui/cva/InfoLabel";
import { Body } from "@/ui/typography/Body";

type FieldCopy = {
  labelKey: string;
  labelDefault: string;
  hintKey?: string;
  hintDefault?: string;
  requiredKey?: string;
  requiredDefault?: string;
  autoComplete: string;
  inputMode?: "url" | "text";
};

const FIELD_COPY: Record<ProBuyerApplicationField, FieldCopy> = {
  companyName: {
    labelKey: "pro-buyer-application:buyers-field-company-name",
    labelDefault: "Bedrijfsnaam",
    autoComplete: "organization",
  },
  registrationNumber: {
    labelKey: "pro-buyer-application:buyers-field-registration-number",
    labelDefault: "KvK-nummer",
    hintKey: "pro-buyer-application:buyers-field-registration-number-hint",
    hintDefault: "Buiten Nederland: je registratienummer (HRB, SIRET, KBO).",
    autoComplete: "off",
  },
  website: {
    labelKey: "pro-buyer-application:buyers-field-website",
    labelDefault: "Website, portfolio of Instagram",
    hintKey: "pro-buyer-application:buyers-field-website-hint",
    hintDefault: "Eén link is genoeg.",
    requiredKey: "pro-buyer-application:buyers-field-website-required",
    requiredDefault:
      "Vul een website, portfolio of Instagram-account in. Zonder kunnen we je aanvraag niet beoordelen.",
    autoComplete: "url",
    inputMode: "url",
  },
};

const EMPTY_VALUES: ProBuyerApplicationInput = {
  companyName: "",
  registrationNumber: "",
  website: "",
};

type BlockView = "form" | "sent" | "rate_limited" | "pro_buyer";

const BUSINESS_EMAIL = "business@whoppah.com";

/** Fields that are empty after trimming; the only client-side validation. */
export const emptyProBuyerFields = (
  values: ProBuyerApplicationInput
): ProBuyerApplicationField[] =>
  PRO_BUYER_APPLICATION_FIELDS.filter(field => values[field].trim() === "");

const Card = ({
  children,
  className,
  testId,
}: {
  children: React.ReactNode;
  className?: string;
  testId: string;
}) => (
  <div
    className={cn("flex flex-col gap-4 rounded-[6px] p-6 md:p-8", className)}
    data-testid={testId}
  >
    {children}
  </div>
);

const ProBuyerCard = () => {
  const { t } = useTranslation();

  return (
    <Card className="bg-flavour-50" testId="pro-buyer-member-card">
      <InfoLabel
        color="brand"
        variant="light"
        size="small"
        corners="rounded"
        className="self-start"
      >
        Pro Buyer
      </InfoLabel>
      <Heading as="h2" size="h4" className="text-brand-600">
        {t("pro-buyer-application:buyers-member-title", "Je bent Pro Buyer")}
      </Heading>
      <Body as="p">
        {t(
          "pro-buyer-application:buyers-member-body",
          "Je ziet Pro Buyer-prijzen bij deelnemende verkopers zodra je bent ingelogd, in je gewone account. Je herkent ze aan het groene label Pro Buyer."
        )}
      </Body>
      <Body as="p" color="muted">
        {t(
          "pro-buyer-application:buyers-member-launch-line",
          "Je ziet je Pro Buyer-prijs op items van verkopers die Pro Buyer-korting geven. In de zoekresultaten toon je met één klik alleen die items."
        )}
      </Body>
    </Card>
  );
};

const LoggedOutBlock = () => {
  const { t } = useTranslation();
  const query = { returnUrl: PRO_BUYER_APPLY_RETURN_URL };
  const rememberScroll = () => {
    try {
      sessionStorage.setItem(PRO_BUYER_SCROLL_FLAG, "1");
    } catch {
      // Storage blocked: the return still lands on /pro-buyer, just at the top.
    }
  };

  return (
    <Card className="bg-brand-100" testId="pro-buyer-apply-logged-out">
      <Heading as="h2" size="h2">
        {t("pro-buyer-application:buyers-apply-title", "Pro Buyer aanvragen")}
      </Heading>
      <Body as="p">
        {t(
          "pro-buyer-application:buyers-apply-logged-out-body",
          "Pro Buyer hoort bij je Whoppah-account. Log in of maak een account; daarna staat hier het formulier, met je naam en e-mail al ingevuld."
        )}
      </Body>
      <div className="flex flex-col gap-3 md:flex-row">
        <LinkButton
          href={`${ACCOUNT_LOGIN_ROUTE}?${new URLSearchParams(query)}`}
          appearance="solid"
          color="brand"
          className="w-full text-center md:w-auto"
          onClick={rememberScroll}
        >
          {t("pro-buyer-application:buyers-login", "Inloggen")}
        </LinkButton>
        <LinkButton
          href={`${ACCOUNT_SIGNUP_ROUTE}?${new URLSearchParams(query)}`}
          appearance="outlined"
          color="brand"
          className="w-full text-center md:w-auto"
          onClick={rememberScroll}
        >
          {t("pro-buyer-application:buyers-signup", "Account aanmaken")}
        </LinkButton>
      </div>
      <Body as="p" className="text-sm text-grey-700">
        {t(
          "pro-buyer-application:buyers-apply-logged-out-note",
          "Heb je al een zakelijk verkopersaccount? Log daarmee in. Pro Buyer komt er als status bij."
        )}
      </Body>
    </Card>
  );
};

const ApplicationForm = ({
  onResult,
}: {
  onResult: (view: Exclude<BlockView, "form">, sentTo?: string) => void;
}) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { fullName, email } = useMe();
  const [values, setValues] = useState<ProBuyerApplicationInput>(EMPTY_VALUES);
  const [invalid, setInvalid] = useState<ProBuyerApplicationField[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field: ProBuyerApplicationField, value: string) => {
    setValues(current => ({ ...current, [field]: value }));

    if (value.trim() !== "") {
      setInvalid(current => current.filter(item => item !== field));
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const empty = emptyProBuyerFields(values);
    setInvalid(empty);

    if (empty.length > 0) {
      return;
    }

    setSubmitting(true);

    const result = await submitProBuyerApplication(token, {
      companyName: values.companyName.trim(),
      registrationNumber: values.registrationNumber.trim(),
      website: values.website.trim(),
    });

    setSubmitting(false);

    if (result.ok) {
      onResult("sent", email);

      return;
    }

    if (result.code === "RATE_LIMITED") {
      onResult("rate_limited");

      return;
    }

    if (result.code === "ALREADY_PRO_BUYER") {
      onResult("pro_buyer");

      return;
    }

    addToast(
      t(
        "pro-buyer-application:buyers-submit-failed",
        "Versturen is niet gelukt, probeer het opnieuw"
      ),
      { appearance: "error" }
    );
  };

  return (
    <Card className="bg-brand-100" testId="pro-buyer-apply-form">
      <Heading as="h2" size="h2">
        {t("pro-buyer-application:buyers-apply-title", "Pro Buyer aanvragen")}
      </Heading>
      <Body as="p">
        {t(
          "pro-buyer-application:buyers-form-intro",
          "Drie velden. We kijken je inschrijving en website na en mailen je binnen een paar werkdagen."
        )}
      </Body>
      <div
        className="flex flex-col rounded-[4px] bg-white p-4"
        data-testid="pro-buyer-account-card"
      >
        <span className="font-title text-base font-bold text-black">
          {fullName}
        </span>
        <span className="font-body text-sm text-grey-700">
          <span className="break-all">{email}</span>
          {" · "}
          <Link href={ACCOUNT_SETTINGS_ROUTE} className="underline">
            {t(
              "pro-buyer-application:buyers-email-change-link",
              "wijzig in Persoonlijke gegevens"
            )}
          </Link>
        </span>
      </div>
      <form className="flex flex-col gap-4" noValidate onSubmit={handleSubmit}>
        {PRO_BUYER_APPLICATION_FIELDS.map(field => {
          const copy = FIELD_COPY[field];
          const isInvalid = invalid.includes(field);
          const inputId = `pro-buyer-${field}`;
          const helperId = `${inputId}-helper`;

          return (
            <div key={field} className="flex flex-col gap-1">
              <label
                htmlFor={inputId}
                className="font-body text-base font-bold text-black"
              >
                {t(copy.labelKey, copy.labelDefault)}
              </label>
              <input
                id={inputId}
                name={field}
                type="text"
                inputMode={copy.inputMode}
                autoComplete={copy.autoComplete}
                maxLength={PRO_BUYER_FIELD_MAX_LENGTH[field]}
                value={values[field]}
                aria-invalid={isInvalid}
                aria-describedby={helperId}
                onChange={event => handleChange(field, event.target.value)}
                className={cn(
                  "h-11 w-full rounded-[4px] border bg-white px-4 font-body text-base text-black focus:outline-none focus:ring-2 focus:ring-curious-200",
                  isInvalid
                    ? "border-attention-500"
                    : "border-grey-500 hover:border-brand-500 focus:border-brand-500"
                )}
              />
              {isInvalid ? (
                <span
                  id={helperId}
                  className="font-body text-sm text-attention-500"
                >
                  {copy.requiredKey
                    ? t(copy.requiredKey, copy.requiredDefault ?? "")
                    : t(
                        "pro-buyer-application:buyers-field-required",
                        "Vul dit veld in"
                      )}
                </span>
              ) : (
                copy.hintKey && (
                  <span
                    id={helperId}
                    className="font-body text-sm text-grey-700"
                  >
                    {t(copy.hintKey, copy.hintDefault ?? "")}
                  </span>
                )
              )}
            </div>
          );
        })}
        <InfoPanel
          color="brand"
          corners="rounded"
          body={
            <span className="font-body text-sm">
              {t(
                "pro-buyer-application:buyers-info-panel",
                "We gebruiken dit alleen om je aanvraag te beoordelen."
              )}{" "}
              <Link href={PRIVACY_ROUTE} className="underline" target="_blank">
                {t(
                  "pro-buyer-application:buyers-privacy-link",
                  "Privacybeleid"
                )}
              </Link>
            </span>
          }
        />
        <Button
          type="submit"
          appearance="solid"
          color="brand"
          loading={submitting}
          className="w-full md:w-auto md:self-start"
        >
          {t("pro-buyer-application:buyers-submit", "Aanvraag versturen")}
        </Button>
      </form>
    </Card>
  );
};

/**
 * WHO-5688. The application block at #aanvragen. Signed out: login and
 * signup, both returning here. Signed in: the three-field form, or the
 * green member card when `me.proBuyer` is true. The thank-you card is local
 * state only, so a revisit shows the form again (nothing is stored).
 */
export const ProBuyerApplicationBlock = () => {
  const { t } = useTranslation();
  const status = useProBuyerStatus();
  const [view, setView] = useState<BlockView>("form");
  const [sentTo, setSentTo] = useState("");

  const handleResult = (next: Exclude<BlockView, "form">, address?: string) => {
    setSentTo(address ?? "");
    setView(next);
  };

  const content = (() => {
    if (status.state === "loading") {
      return (
        <div
          className="h-64 animate-pulse rounded-[6px] bg-brand-100"
          data-testid="pro-buyer-apply-loading"
        />
      );
    }

    if (status.state === "signed_out") {
      return <LoggedOutBlock />;
    }

    if (status.proBuyer || view === "pro_buyer") {
      return <ProBuyerCard />;
    }

    if (view === "sent") {
      const fixLine = t("pro-buyer-application:buyers-sent-fix", {
        defaultValue:
          "Iets verkeerd ingevuld? Mail {{address}} met je bedrijfsnaam.",
        address: BUSINESS_EMAIL,
      });
      const [fixBefore, fixAfter] = fixLine.split(BUSINESS_EMAIL);

      return (
        <Card className="bg-brand-100" testId="pro-buyer-apply-sent">
          <Heading as="h2" size="h2">
            {t(
              "pro-buyer-application:buyers-sent-title",
              "Bedankt, je aanvraag is verstuurd"
            )}
          </Heading>
          <Body as="p">
            {t("pro-buyer-application:buyers-sent-body", {
              defaultValue:
                "We kijken je inschrijving en website na en mailen je binnen een paar werkdagen op {{email}}. Je hoeft nu niets te doen.",
              email: sentTo,
            })}
          </Body>
          <Body as="p" className="text-sm text-grey-700">
            {fixBefore}
            <a href={`mailto:${BUSINESS_EMAIL}`} className="underline">
              {BUSINESS_EMAIL}
            </a>
            {fixAfter}
          </Body>
          <LinkButton
            href="/"
            appearance="outlined"
            color="brand"
            className="w-full text-center md:w-auto md:self-start"
          >
            {t("pro-buyer-application:buyers-sent-continue", "Verder winkelen")}
          </LinkButton>
        </Card>
      );
    }

    if (view === "rate_limited") {
      return (
        <Card className="bg-brand-100" testId="pro-buyer-apply-rate-limited">
          <Heading as="h2" size="h2">
            {t(
              "pro-buyer-application:buyers-apply-title",
              "Pro Buyer aanvragen"
            )}
          </Heading>
          <Body as="p">
            {t(
              "pro-buyer-application:buyers-rate-limited",
              "Je hebt al een aanvraag verstuurd. We nemen contact met je op."
            )}
          </Body>
        </Card>
      );
    }

    return <ApplicationForm onResult={handleResult} />;
  })();

  return (
    <section
      id={PRO_BUYER_APPLY_ANCHOR}
      className="mx-auto w-full max-w-3xl scroll-mt-24 px-4 py-12 md:px-8"
    >
      {content}
    </section>
  );
};
